import { ArcElement, Chart as ChartJS, type ChartOptions, Legend, type Plugin, Tooltip } from 'chart.js'
import type React from 'react'
import { Doughnut } from 'react-chartjs-2'
import { cn } from '../../lib/cva'
import { Text } from '../Text'

ChartJS.register(ArcElement, Tooltip, Legend)

export interface PieChartDataItem {
	label: string
	value: number
	color: string
}

export interface PieChartProps {
	data: PieChartDataItem[]
	title?: string
	showLegend?: boolean
	showTooltips?: boolean
	showPercentages?: boolean
	showValues?: boolean
	centerText?: string
	size?: 'sm' | 'md' | 'lg'
	className?: string
	legendPosition?: 'top' | 'bottom' | 'left' | 'right'
	cutout?: number
}

const sizeConfig = {
	sm: { width: 200, height: 200 },
	md: { width: 250, height: 250 },
	lg: { width: 300, height: 300 },
}

export const PieChart: React.FC<PieChartProps> = ({
	data,
	title,
	showLegend = true,
	showTooltips = true,
	showPercentages = true,
	showValues = true,
	centerText,
	size = 'md',
	className,
	legendPosition = 'right',
	cutout = 50,
}) => {
	const totalValue = data.reduce((sum, item) => sum + item.value, 0)

	const dataWithPercentages = data.map((item) => ({
		...item,
		percentage: totalValue > 0 ? Math.round((item.value / totalValue) * 100) : 0,
	}))

	const chartData = {
		labels: dataWithPercentages.map((item) => item.label),
		datasets: [
			{
				data: dataWithPercentages.map((item) => item.value),
				backgroundColor: dataWithPercentages.map((item) => item.color),
				borderWidth: 2,
				borderColor: '#ffffff',
				hoverBorderWidth: 3,
				hoverBorderColor: '#ffffff',
			},
		],
	}

	const centerTextPlugin: Plugin<'doughnut'> = {
		id: 'centerText',
		beforeDraw: (chart) => {
			if (!centerText) return

			const { width, height, ctx } = chart
			ctx.restore()
			const fontSize = (height / 114).toFixed(2)
			ctx.font = `${fontSize}em sans-serif`
			ctx.textBaseline = 'middle'
			ctx.fillStyle = '#374151'

			const text = centerText
			const textX = Math.round((width - ctx.measureText(text).width) / 2)
			const textY = height / 2

			ctx.fillText(text, textX, textY)
			ctx.save()
		},
	}

	const options: ChartOptions<'doughnut'> = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: `${cutout}%`,
		rotation: 0,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: showTooltips,
				callbacks: {
					label: (context) => {
						const item = dataWithPercentages[context.dataIndex]
						const parts = []
						parts.push(item.label)
						if (showValues) parts.push(`${item.value}`)
						if (showPercentages) parts.push(`(${item.percentage}%)`)
						return parts.join(' - ')
					},
				},
			},
		},
	}

	const CustomLegend = () => (
		<div className='flex flex-col space-y-2'>
			{dataWithPercentages.map((item) => (
				<div key={item.label} className='flex items-center space-x-3'>
					<div className='w-4 h-4 rounded-sm flex-shrink-0' style={{ backgroundColor: item.color }} />
					<div className='flex-1 text-sm text-gray-700'>
						<span>{item.label}</span>
						{(showValues || showPercentages) && (
							<span className='text-gray-500 ml-1'>
								-{showValues && ` ${item.value}`}
								{showPercentages && ` (${item.percentage}%)`}
							</span>
						)}
					</div>
				</div>
			))}
		</div>
	)

	const chartSize = sizeConfig[size]

	return (
		<div className={cn('flex flex-col', className)}>
			{title && (
				<Text variant='h5' weight='semibold' className='mb-4'>
					{title}
				</Text>
			)}

			<div
				className={cn(
					'flex items-start',
					legendPosition === 'bottom' && 'flex-col',
					legendPosition === 'top' && 'flex-col-reverse',
					legendPosition === 'left' && 'flex-row-reverse',
					legendPosition === 'right' && 'flex-row',
				)}>
				<div className='flex-shrink-0 ml-6' style={{ width: chartSize.width, height: chartSize.height }}>
					<Doughnut data={chartData} options={options} plugins={centerText ? [centerTextPlugin] : []} />
				</div>

				{showLegend && (
					<div
						className={cn(
							'flex-shrink-0',
							(legendPosition === 'left' || legendPosition === 'right') && 'ml-6',
							(legendPosition === 'top' || legendPosition === 'bottom') && 'mt-4',
						)}>
						<CustomLegend />
					</div>
				)}
			</div>
		</div>
	)
}

export default PieChart
